import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { mask } from 'remask';
import app from '../../../package.json';
//
import { formatDate } from '../../functions/formatDate';
import { getDateDiffText } from '../../functions/getDateDiffText';
import { toCurrency } from '../../functions/toCurrency';
//
import axios from 'axios';
import apiPlay from '../../services/apiPlay';
import apiFilas from '../../services/apiFilas';
import { PORTAL_BASE, aceitaRecorrencia, ehDoPortal } from '../../services/portalFaturas';
//
import Base64Img from '../../components/Base64Img';
import LoadingCircle from '../../components/LoadingCircle';
import StatusFatura from '../../components/StatusFatura';
//
import { BiTransferAlt } from 'react-icons/bi';
import {
  IoAlertCircle,
  IoBarcodeSharp,
  IoCardOutline,
  IoReload,
  IoChevronDown,
  IoChevronForward,
  IoPersonOutline,
  IoConstructOutline,
  IoInformationCircle,
  IoCheckmarkCircle,
  IoReceiptOutline,
  IoDocumentTextOutline,
  IoSunnyOutline,
  IoMoonOutline,
  IoSyncCircleOutline,
  IoWalletOutline,
} from 'react-icons/io5';
//
import TermosDialog from '../../components/TermosDialog';
import FaturaFallback, { FaturaFallbackReason } from '../FaturaFallback';
import Redirect from '../Redirect';
import Erro from '../Erro';
import styles from './fatura.module.css';
import { Cartao, Fatura as FaturaType, FaturaStatus } from '../../types/fatura';
import {
  AutorizacaoCompleta,
  AutorizacaoResumo,
  ListarPorIccidResponse,
} from '../../types/pixAutomatico';
import {
  FORMA_PAGAMENTO_PADRAO,
  PIX_ATIVO,
  PIX_AUTOMATICO_ATIVO,
  PIX_AVISO,
} from '../../config/pagamento';

// Lazy load das telas de pagamento — renderizadas sob demanda
const TelaPix = lazy(() => import('../../components/TelaPix'));
const TelaBoleto = lazy(() => import('../../components/TelaBoleto'));
const TelaCredito = lazy(() => import('../../components/TelaCredito'));
const TelaRecorrencia = lazy(() => import('../../components/TelaRecorrencia'));
const TelaPixAutomatico = lazy(() => import('../../components/TelaPixAutomatico'));

type DemoView = 'fatura' | 'redirect' | 'fallback' | 'erro';
type FallbackState = { reason: FaturaFallbackReason; detail?: string } | null;

// Descritor de uma forma de pagamento alternativa ao PIX avulso.
interface MetodoPagamento {
  key: string;
  icon: React.ReactNode;
  title: string;
  desc: string;
  show: boolean;
  badge?: string | null;
}

// Considera fatura "200 válida" quando vier com pelo menos um identificador essencial.
// Se vier 200 mas com payload vazio/inválido, tratamos como fallback em vez de tela vazia.
/* =====================================================================
   FATURA DO PORTAL DE FRANQUIAS — 18/09/2026
   =====================================================================
   Só é chamada quando o Infiniti responde 404. O portal devolve o MESMO
   formato (`value`, `dueDate`, `status`, `encodedimage`, `payload`,
   `codigoboleto`, `barcode`…) — é o contrato que esta tela já lê, e foi
   por isso que ele foi copiado lá, e não inventado um novo aqui.

   `fetch` puro em vez do `apiPlay`: aquele cliente carrega o token da
   Play e aponta para outro domínio. Esta chamada não leva credencial
   nenhuma — o segredo é o próprio `payid`, como no Infiniti.

   O endereço sai do build (`REACT_APP_PORTAL_BASE`) e mora em
   `services/portalFaturas`, junto das chamadas de pagamento — uma
   constante só, para o domínio não divergir entre a leitura e a ação.
   ===================================================================== */
async function buscarNoPortal(payId: string | null): Promise<unknown | null> {
  if (!payId) return null;
  const resposta = await fetch(
    `${PORTAL_BASE}/api/publico/fatura/${encodeURIComponent(payId)}`,
    { headers: { Accept: 'application/json' } },
  );
  if (!resposta.ok) return null;
  const fatura = await resposta.json();

  /* A MARCA DE ORIGEM (23/09/2026). O que a tela pode oferecer depende de
     QUEM respondeu: o cartão de uma fatura do portal é pago no portal, e
     só a mensalidade dele aceita recorrência. Quem sabe por qual caminho
     a resposta chegou é aqui — o servidor não tem como dizer isso, e
     adivinhar pela presença de `origem` deixaria a tela refém de um campo
     que um dia pode existir dos dois lados. */
  return fatura && typeof fatura === 'object'
    ? { ...fatura, doPortal: true }
    : fatura;
}

function isFaturaValida(data: unknown): data is FaturaType {
  if (!data || typeof data !== 'object') return false;
  const f = data as FaturaType;
  return !!(f.companyid || f.value !== undefined || f.status);
}

// ── Parsing inteligente da descrição ──
type DescPhoneLine = { phone: string; plan: string };
type DescTextLine = { text: string };
type DescLine = DescPhoneLine | DescTextLine;

type DescData =
  | { type: 'empty'; lines: never[] }
  | { type: 'simple'; text: string }
  | { type: 'phones'; lines: DescLine[] }
  | { type: 'multi'; lines: string[] };

function parseDescription(desc?: string): DescData {
  if (!desc) return { type: 'empty', lines: [] };
  const rawLines = desc
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const phoneRegex = /^(\(?\d{2}\)?\s?\d{4,5}-?\d{4})\s*[-–]\s*(.+)$/;
  const parsed: DescLine[] = rawLines.map<DescLine>((line) => {
    const m = line.match(phoneRegex);
    if (m) return { phone: m[1].trim(), plan: m[2].trim() };
    return { text: line };
  });

  const hasPhones = parsed.some((p): p is DescPhoneLine => 'phone' in p);
  if (hasPhones) return { type: 'phones', lines: parsed };
  if (rawLines.length === 1) return { type: 'simple', text: rawLines[0] };
  return { type: 'multi', lines: rawLines };
}

const PAID_STATUSES: FaturaStatus[] = ['RECEIVED', 'RECEIVED_IN_CASH', 'CONFIRMED'];
const REFUNDED_STATUSES: FaturaStatus[] = ['REFUNDED', 'REFUND_REQUESTED'];
const CANCELLED_STATUSES: FaturaStatus[] = ['CANCELLED'];

interface ListaCartoesResponse {
  status: boolean;
  cartoes: Cartao[];
}

export default function Fatura() {
  const [searchParams] = useSearchParams();
  const payId = searchParams.get('payid');

  const [formaPagamento, setFormaPagamento] = useState(FORMA_PAGAMENTO_PADRAO);
  const [loadingDados, setLoadingDados] = useState(true);
  const [fatura, setFatura] = useState<FaturaType>({});
  const [isRecActive, setIsRecActive] = useState(false);
  const [cartoes, setCartoes] = useState<Cartao[]>([]);
  const [pixAutoAuth, setPixAutoAuth] = useState<AutorizacaoResumo | AutorizacaoCompleta | null>(
    null
  );
  const [showOtherMethods, setShowOtherMethods] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showDesc, setShowDesc] = useState(false);
  const [showDemoPanel, setShowDemoPanel] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [fallback, setFallback] = useState<FallbackState>(null);
  const [demoView, setDemoView] = useState<DemoView>('fatura');

  const isDev = process.env.NODE_ENV === 'development';
  // Demo só acessível por flag explícita em dev: /?demo=1
  const isDemo = !payId && isDev && searchParams.get('demo') === '1';
  const isAdicional = fatura.payment === 'ADICIONAL';
  /* CLUBE DE BENEFÍCIOS (24/09/2026). Cobrança da assinatura do clube, criada
     pelo app — o Infiniti resolve o payid pela fonte `clubecerto`. Não é
     plano nem linha: a descrição vem do Asaas ("Clube X - adesão mensal") e
     não existe NFCom pra ela, então o link da nota some. */
  const isClube = fatura.payment === 'CLUBE';
  const usaDescricao = isAdicional || isClube;

  const isPaid = useMemo(
    () => !!fatura.status && PAID_STATUSES.includes(fatura.status),
    [fatura.status]
  );
  const isOverdue = fatura.status === 'OVERDUE';
  const isRefunded = !!fatura.status && REFUNDED_STATUSES.includes(fatura.status);
  const isCancelled = !!fatura.status && CANCELLED_STATUSES.includes(fatura.status);
  const hidePayment = isPaid || isRefunded || isCancelled;

  const pixAutoAtivo =
    !!pixAutoAuth && (pixAutoAuth.status === 'ACTIVE' || pixAutoAuth.status === 'CREATED');

  const descData = useMemo(() => parseDescription(fatura.description), [fatura.description]);

  const containerClass = useMemo(() => {
    if (isPaid) return styles['container-paid'];
    if (isOverdue) return styles['container-overdue'];
    if (isRefunded) return styles['container-refunded'];
    if (fatura.status === 'PENDING') return styles['container-pending'];
    return styles['container'];
  }, [isPaid, isOverdue, isRefunded, fatura.status]);

  const statusClass = useMemo(() => {
    if (isPaid) return styles['status-paid'];
    if (isOverdue) return styles['status-overdue'];
    if (isRefunded) return styles['status-refunded'];
    return styles['status-pending'];
  }, [isPaid, isOverdue, isRefunded]);

  useEffect(() => {
    if (!payId && !isDemo) {
      // Sem payId e fora do modo demo: a renderização cai em <Redirect /> abaixo,
      // não há nada para carregar.
      return;
    }
    if (!payId) {
      console.log(`Fatura v.${app.version} (modelo demonstrativo)`);
      const faturaDemo: FaturaType = {
        status: 'PENDING',
        value: 615.0,
        dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        description:
          '(35) 92003-8028 - ICON UNIVERSITÁRIO\n(35) 99812-5541 - CONTROLE 25GB\n(35) 98804-7712 - CONTROLE 15GB\n(35) 99148-3390 - CONTROLE 15GB\n(35) 99761-0824 - CONTROLE 15GB\n(62) 99445-1182 - CONTROLE 25GB',
        contafatura: true,
        nome: 'MGF Sul Construtora e Serviços Ltda',
        cpf: '10617255000151',
        email: 'mgfsul@hotmail.com',
        msisdn: '5535920038028',
        plandescription: 'ICON UNIVERSITÁRIO',
        payment: 'MENSAL',
        nomeempresa: 'Easy Brasil Telecom',
        cnpj: '43470720000123',
        companyid: 'demo-001',
        iccid: '8955010000000123456',
        logo: null,
        encodedimage: null,
        payload:
          '00020126580014br.gov.bcb.pix0136a1b2c3d4-e5f6-7890-abcd-ef1234567890520400005303986540615.005802BR5925EASY BRASIL TELECOM LTDA6008BRASILIA62070503***6304DEMO',
        link: 'https://www.asaas.com/b/pdf/demo/demo123',
        codigoboleto: '23793.38128 60000.000003 00000.000402 1 00000000061500',
        barcode: '23791000000006150033812860000000000000000040',
        paymentDate: null,
        billingType: null,
      };
      setFatura(faturaDemo);
      setCartoes([]);
      setIsRecActive(false);
      setLoadingDados(false);
      return;
    }

    console.log(`Fatura v.${app.version}`);
    const cancelToken = axios.CancelToken.source();

    function cardsRecorrencia(cpf?: string, companyid?: string, msisdn?: string) {
      if (!cpf || !companyid || !msisdn) return;
      apiPlay
        .post<ListaCartoesResponse>('/api/recorrencia/listaCartoes', { cpf, companyid, msisdn })
        .then((response) => {
          setIsRecActive(response.data.status);
          setCartoes(response.data.cartoes);
        })
        .catch((error) => {
          console.log('/api/recorrencia/listaCartoes | error', error);
        });
    }

    function checaPixAutomatico(iccid?: string) {
      if (!iccid) return;
      apiFilas
        .get<ListarPorIccidResponse>(`/pix-automatico/iccid/${encodeURIComponent(iccid)}`)
        .then((response) => {
          const lista = response.data?.data ?? [];
          const ativa = lista.find((a) => a.status === 'ACTIVE' || a.status === 'CREATED');
          if (ativa) setPixAutoAuth(ativa);
        })
        .catch((error) => {
          console.log('/pix-automatico/iccid | error', error);
        });
    }

    apiPlay
      .post<FaturaType>('/api/asaasfatura', { payid: payId }, { cancelToken: cancelToken.token })
      .then((response) => {
        // Caminho feliz — 200 com fatura válida sempre vai para a tela Fatura.
        if (!isFaturaValida(response.data)) {
          setFallback({
            reason: 'invalid',
            detail: 'Resposta sem dados de fatura.',
          });
          setLoadingDados(false);
          return;
        }
        setFatura(response.data);
        cardsRecorrencia(response.data.cpf, response.data.companyid, response.data.msisdn);
        checaPixAutomatico(response.data.iccid);
        setLoadingDados(false);
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          console.log('Request Cancelado');
          return;
        }
        console.log(error);
        const status: number | undefined = error?.response?.status;
        const detail = error?.response?.data?.message || error?.message;

        /* PORTAL DE FRANQUIAS — SEGUNDA TENTATIVA (18/09/2026)
           ---------------------------------------------------
           As cobranças do portal de franquias (mensalidade da loja,
           adesão, pedido de chips, SMS, e a fatura que a franquia emite
           ao cliente dela) vivem em OUTRO banco, que o resolvedor do
           Infiniti não conhece — e todo link `?payid=` delas caía aqui
           como 404 "Pagamento não encontrado".

           A tentativa é EXCLUSIVAMENTE no 404: o que já funciona hoje
           responde 200 no Infiniti e nunca chega nesta linha. Qualquer
           outro erro (rede, 500, 422) segue direto para o fallback, sem
           uma segunda chamada que só atrasaria a tela de erro. */
        if (status === 404) {
          buscarNoPortal(payId)
            .then((doPortal) => {
              if (doPortal && isFaturaValida(doPortal)) {
                setFatura(doPortal);
                setLoadingDados(false);
                return;
              }
              setFallback({ reason: 'not_found', detail });
              setLoadingDados(false);
            })
            .catch(() => {
              setFallback({ reason: 'not_found', detail });
              setLoadingDados(false);
            });
          return;
        }

        let reason: FaturaFallbackReason = 'unknown';
        if (!error?.response) reason = 'network';
        else if (status === 404) reason = 'not_found';
        else if (status === 410) reason = 'expired';
        else if (status === 422 || status === 400) reason = 'invalid';
        setFallback({ reason, detail });
        setLoadingDados(false);
      });

    return () => {
      cancelToken.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sem payId → tela de redirecionamento (demo só com ?demo=1 em dev) ──
  if (!payId && !isDemo) {
    return <Redirect />;
  }

  // ── Demo: simulação manual de telas de erro ──
  if (isDemo && demoView === 'redirect') return <Redirect />;
  if (isDemo && demoView === 'fallback') {
    return <FaturaFallback reason="not_found" onRetry={() => setDemoView('fatura')} />;
  }
  if (isDemo && demoView === 'erro') {
    return (
      <Erro
        error={new Error('Erro simulado pelo painel demo')}
        onRetry={() => setDemoView('fatura')}
      />
    );
  }

  if (loadingDados) {
    return (
      <div className={styles['loading']}>
        <LoadingCircle />
      </div>
    );
  }

  // ── Fatura indisponível: 4xx, 5xx, network ou 200 inválido ──
  if (fallback) {
    return <FaturaFallback reason={fallback.reason} detail={fallback.detail} />;
  }

  const renderPaymentContent = () => {
    const key = formaPagamento;
    return (
      <Suspense
        fallback={
          <div className={styles['payment-content']}>
            <LoadingCircle />
          </div>
        }
      >
        <div className={styles['payment-content']} key={key}>
          {PIX_ATIVO && key === '0' && <TelaPix fatura={fatura} />}
          {key === '1' && <TelaBoleto fatura={fatura} />}
          {key === '2' && <TelaCredito fatura={fatura} payId={payId} />}
          {key === '3' && (
            <TelaRecorrencia
              fatura={fatura}
              payId={payId}
              cartoesInitial={cartoes}
              isRecActive={isRecActive}
              onToggleRec={setIsRecActive}
            />
          )}
          {key === '4' && (
            <TelaPixAutomatico
              fatura={fatura}
              autorizacaoExistente={pixAutoAuth}
              onAtualizou={setPixAutoAuth}
            />
          )}
        </div>
      </Suspense>
    );
  };

  // Formas de pagamento alem do PIX avulso. `show` combina a disponibilidade
  // por dados da fatura (msisdn/iccid) com as chaves de config/pagamento.
  const metodosAlternativos: MetodoPagamento[] = [
    {
      key: '2',
      icon: <IoCardOutline className={styles['alt-option-icon']} />,
      title: 'Cartão de crédito',
      desc: 'Aprovação imediata',
      show: true,
    },
    {
      key: '3',
      icon: <IoReload className={styles['alt-option-icon']} />,
      title: 'Cobrança recorrente',
      desc: 'Débito mensal no cartão',
      /* `msisdn` é a linha do cliente no Infiniti e é o que dizia, lá,
         que existia recorrência para aquela conta. Fatura do portal não
         tem linha nenhuma — quem manda é a ORIGEM: só a mensalidade se
         repete todo mês. Adesão, pedido de chip e SMS são compra única, e
         a adesão ainda vem com kit de material junto. */
      show: ehDoPortal(fatura) ? aceitaRecorrencia(fatura) : !!fatura.msisdn,
    },
    {
      key: '4',
      icon: <IoSyncCircleOutline className={styles['alt-option-icon']} />,
      title: 'Pix automático',
      desc: 'Débito mensal direto no banco',
      /* O Pix Automático desta tela é o do Infiniti, amarrado ao `iccid`.
         Para a fatura do portal ele ainda não existe: é outro produto,
         com autorização própria e webhooks próprios, e o portal não tem o
         lado dele. Oferecer aqui seria repetir o erro do cartão —
         mostrar um caminho que termina em erro. */
      show: !ehDoPortal(fatura) && !!fatura.iccid && PIX_AUTOMATICO_ATIVO,
      badge:
        pixAutoAtivo && pixAutoAuth
          ? pixAutoAuth.status === 'ACTIVE'
            ? 'Ativo'
            : 'Aguardando'
          : null,
    },
    {
      key: '1',
      icon: <IoBarcodeSharp className={styles['alt-option-icon']} />,
      title: 'Boleto bancário',
      desc: 'Até 3 dias para compensar',
      show: true,
    },
  ];

  const renderOpcaoMetodo = (metodo: MetodoPagamento) => (
    <button
      key={metodo.key}
      className={styles['alt-option']}
      onClick={() => {
        setFormaPagamento(metodo.key);
        setShowOtherMethods(false);
      }}
      type="button"
    >
      {metodo.icon}
      <div className={styles['alt-option-info']}>
        <span className={styles['alt-option-title']}>
          {metodo.title}
          {metodo.badge && (
            <span className={styles['alt-option-badge']}>{metodo.badge}</span>
          )}
        </span>
        <span className={styles['alt-option-desc']}>{metodo.desc}</span>
      </div>
      <IoChevronForward className={styles['alt-option-arrow']} />
    </button>
  );

  const renderDescription = () => {
    if (!fatura.description) return null;

    if (descData.type === 'simple') {
      return <span className={styles['desc-simple']}>{descData.text}</span>;
    }

    if (descData.type === 'phones') {
      const phoneLines = descData.lines.filter((l): l is DescPhoneLine => 'phone' in l);
      const count = phoneLines.length;

      if (count === 1) {
        const line = phoneLines[0];
        return (
          <div className={styles['desc-section']}>
            <div className={styles['desc-lines']}>
              <div className={styles['desc-line-item']}>
                <span className={styles['desc-line-phone']}>{line.phone}</span>
                <span className={styles['desc-line-plan']}>{line.plan}</span>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className={styles['desc-section']}>
          <button
            className={styles['desc-toggle']}
            onClick={() => setShowDesc((p) => !p)}
            type="button"
          >
            <span className={styles['desc-toggle-count']}>{count} linhas</span>
            <span>inclusas nesta fatura</span>
            <IoChevronDown
              className={`${styles['desc-chevron']} ${
                showDesc ? styles['desc-chevron-open'] : ''
              }`}
            />
          </button>
          {showDesc && (
            <div className={styles['desc-lines']}>
              {descData.lines.map((item, i) =>
                'phone' in item ? (
                  <div key={i} className={styles['desc-line-item']}>
                    <span className={styles['desc-line-phone']}>{item.phone}</span>
                    <span className={styles['desc-line-plan']}>{item.plan}</span>
                  </div>
                ) : (
                  <div key={i} className={styles['desc-line-item']}>
                    <span className={styles['desc-line-plan']}>{item.text}</span>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      );
    }

    if (descData.type === 'multi') {
      return (
        <div className={styles['desc-section']}>
          <button
            className={styles['desc-toggle']}
            onClick={() => setShowDesc((p) => !p)}
            type="button"
          >
            <span>Detalhes da fatura</span>
            <IoChevronDown
              className={`${styles['desc-chevron']} ${
                showDesc ? styles['desc-chevron-open'] : ''
              }`}
            />
          </button>
          {showDesc && (
            <div className={styles['desc-lines']}>
              {descData.lines.map((line, i) => (
                <div key={i} className={styles['desc-line-item']}>
                  <span className={styles['desc-line-plan']}>{line}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className={containerClass}>
      <div className={styles['fatura']}>
        {/* ─── Top Bar ─── */}
        <header className={styles['topbar']}>
          <div className={styles['topbar-left']}>
            {fatura.logo && (
              <div className={styles['topbar-logo']}>
                <Base64Img data={fatura.logo} className={styles['topbar-logo-img']} />
              </div>
            )}
            <span className={styles['topbar-name']}>
              {fatura.nomeempresa && fatura.nomeempresa.toUpperCase()}
            </span>
          </div>
          <a
            className={styles['topbar-help']}
            href={`https://atendimento.operadora.app.br/?companyId=${fatura.companyid}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Ajuda
          </a>
        </header>

        {/* ─── Hero: Valor ─── */}
        <div className={styles['hero']}>
          <div className={statusClass}>
            <StatusFatura fatura={fatura} />
          </div>

          <div className={styles['hero-amount']}>
            <span className={styles['hero-currency']}>R$</span>
            <span className={styles['hero-value']}>
              {fatura.value ? toCurrency(fatura.value).replace(/R\$\s?/, '') : '0,00'}
            </span>
          </div>

          <div className={styles['hero-meta']}>
            <span className={styles['hero-due']}>
              Venc.{' '}
              {fatura.dueDate
                ? formatDate(`${fatura.dueDate} 00:00:00`.replace(/-/g, '/'))
                : '—'}
            </span>
          </div>

          {fatura.dueDate && !hidePayment && (
            <span className={styles['hero-countdown']}>
              {getDateDiffText(fatura.dueDate + ' 00:00:00')}
            </span>
          )}

          {renderDescription()}
        </div>

        {/* ─── Comprovante (quando pago) ─── */}
        {isPaid && (
          <div className={styles['receipt-section']}>
            <div className={styles['receipt-grid']}>
              {fatura.paymentDate && (
                <div className={styles['receipt-item']}>
                  <IoCheckmarkCircle
                    className={styles['receipt-item-icon']}
                    style={{ color: 'var(--color-success)' }}
                  />
                  <div className={styles['receipt-item-info']}>
                    <span className={styles['receipt-item-label']}>Pago em</span>
                    <span className={styles['receipt-item-value']}>
                      {formatDate(`${fatura.paymentDate} 00:00:00`.replace(/-/g, '/'))}
                    </span>
                  </div>
                </div>
              )}
              {fatura.billingType && (
                <div className={styles['receipt-item']}>
                  <IoReceiptOutline className={styles['receipt-item-icon']} />
                  <div className={styles['receipt-item-info']}>
                    <span className={styles['receipt-item-label']}>Forma</span>
                    <span className={styles['receipt-item-value']}>{fatura.billingType}</span>
                  </div>
                </div>
              )}
            </div>
            {!isClube && (
              <Link
                className={styles['receipt-nota']}
                to={`/nota?payid=${encodeURIComponent(payId ?? '')}`}
              >
                <IoDocumentTextOutline />
                <span>Consultar nota fiscal</span>
                <IoChevronForward className={styles['receipt-nota-arrow']} />
              </Link>
            )}
          </div>
        )}

        {/* ─── Detalhes do cliente (colapsável) ─── */}
        <button
          className={styles['details-toggle']}
          onClick={() => setShowDetails((p) => !p)}
          type="button"
        >
          <IoPersonOutline className={styles['details-toggle-icon']} />
          <div className={styles['details-toggle-info']}>
            <span className={styles['details-toggle-name']}>{fatura.nome || 'Cliente'}</span>
            {fatura.msisdn && (
              <span className={styles['details-toggle-phone']}>
                {mask(fatura.msisdn.slice(2), '(99) 99999-9999')}
              </span>
            )}
          </div>
          <IoChevronDown
            className={`${styles['details-chevron']} ${
              showDetails ? styles['details-chevron-open'] : ''
            }`}
          />
        </button>

        {showDetails && (
          <div className={styles['details-panel']}>
            <div className={styles['detail-row']}>
              <span className={styles['detail-label']}>CPF/CNPJ</span>
              <span className={styles['detail-value']}>
                {fatura.cpf
                  ? mask(fatura.cpf, ['999.999.999-99', '99.999.999/9999-99'])
                  : '-'}
              </span>
            </div>
            <div className={styles['detail-row']}>
              <span className={styles['detail-label']}>
                {usaDescricao ? 'Descrição' : 'Plano'}
              </span>
              <span className={styles['detail-value']}>
                {usaDescricao
                  ? fatura.description || fatura.plandescription || '-'
                  : fatura.plandescription || '-'}
              </span>
            </div>
            {fatura.email && (
              <div className={styles['detail-row']}>
                <span className={styles['detail-label']}>E-mail</span>
                <span className={styles['detail-value-muted']}>{fatura.email}</span>
              </div>
            )}
          </div>
        )}

        {/* ─── Pagamento ─── */}
        {!hidePayment && (
          <div className={styles['payment-section']}>
            {/* Explica a ausencia do PIX enquanto a chave estiver desligada. */}
            {!PIX_ATIVO && (
              <div className={styles['alert-pix-off']}>
                <IoInformationCircle size={16} color="var(--color-info)" />
                <p>{PIX_AVISO}</p>
              </div>
            )}

            {!isAdicional && (
              <div className={styles['termos-notice']}>
                <span className={styles['termos-text']}>
                  Ao pagar, você concorda com os{' '}
                  <TermosDialog nomeMvno={fatura.nomeempresa}>
                    <span className={styles['termos-link']}>termos e condições</span>
                  </TermosDialog>
                </span>
                <IoChevronDown className={styles['termos-arrow']} />
              </div>
            )}

            {formaPagamento === '0' && renderPaymentContent()}

            {formaPagamento !== '0' && (
              <>
                {formaPagamento === '3' && (
                  <div className={styles['alert-recurrence']}>
                    <IoAlertCircle size={16} color="var(--color-warning)" />
                    <p>
                      Cobrança no <strong>primeiro cartão</strong>. Se falhar, tentamos o{' '}
                      <strong>segundo</strong> e <strong>terceiro</strong>.
                    </p>
                  </div>
                )}
                {renderPaymentContent()}
              </>
            )}

            {/* PIX ligado: metodos alternativos ficam atras do toggle. */}
            {PIX_ATIVO && formaPagamento === '0' && (
              <div className={styles['alt-methods']}>
                <button
                  className={styles['alt-methods-toggle']}
                  onClick={() => setShowOtherMethods((p) => !p)}
                  type="button"
                  aria-expanded={showOtherMethods}
                >
                  <IoWalletOutline className={styles['alt-methods-icon']} />
                  <span>Outras formas de pagamento</span>
                  <IoChevronDown
                    className={`${styles['alt-chevron']} ${
                      showOtherMethods ? styles['alt-chevron-open'] : ''
                    }`}
                  />
                </button>

                {showOtherMethods && (
                  <div className={styles['alt-methods-list']}>
                    {metodosAlternativos.filter((m) => m.show).map(renderOpcaoMetodo)}
                  </div>
                )}
              </div>
            )}

            {/* PIX desligado: lista sempre aberta, para dar destaque as formas
                de pagamento que seguem disponiveis. */}
            {!PIX_ATIVO &&
              (() => {
                const opcoes = metodosAlternativos.filter(
                  (m) => m.show && m.key !== formaPagamento
                );
                if (opcoes.length === 0) return null;
                return (
                  <div className={styles['alt-methods']}>
                    <span className={styles['alt-methods-title']}>
                      Outras formas de pagamento
                    </span>
                    <div className={styles['alt-methods-list']}>
                      {opcoes.map(renderOpcaoMetodo)}
                    </div>
                  </div>
                );
              })()}

            {PIX_ATIVO && formaPagamento !== '0' && (
              <button
                className={styles['back-pix']}
                onClick={() => setFormaPagamento('0')}
                type="button"
              >
                <BiTransferAlt />
                <span>Pagar com PIX</span>
              </button>
            )}
          </div>
        )}

        {/* ─── Footer ─── */}
        <footer className={styles['footer']}>
          <span className={styles['footer-text']}>
            {fatura.nomeempresa?.toUpperCase()} | CNPJ{' '}
            {fatura.cnpj && mask(fatura.cnpj, ['99.999.999/9999-99'])}
          </span>
        </footer>
      </div>

      {/* ─── Demo Panel ─── */}
      {isDemo && (
        <>
          <button
            className={styles['demo-fab']}
            onClick={() => setShowDemoPanel((p) => !p)}
            type="button"
            title="Painel de demonstração"
          >
            <IoConstructOutline />
          </button>

          {showDemoPanel && (
            <div className={styles['demo-panel']}>
              <span className={styles['demo-panel-title']}>Modo Demonstração</span>
              <div className={styles['demo-panel-group']}>
                <span className={styles['demo-panel-label']}>Tema</span>
                <div className={styles['demo-panel-buttons']}>
                  <button
                    className={`${styles['demo-btn']} ${
                      !darkMode ? styles['demo-btn-active'] : ''
                    }`}
                    style={!darkMode ? { borderColor: '#ff9500', color: '#ff9500' } : {}}
                    onClick={() => {
                      setDarkMode(false);
                      document.documentElement.setAttribute('data-theme', 'light');
                    }}
                    type="button"
                  >
                    <IoSunnyOutline style={{ marginRight: 4 }} /> Claro
                  </button>
                  <button
                    className={`${styles['demo-btn']} ${
                      darkMode ? styles['demo-btn-active'] : ''
                    }`}
                    style={darkMode ? { borderColor: '#007aff', color: '#007aff' } : {}}
                    onClick={() => {
                      setDarkMode(true);
                      document.documentElement.setAttribute('data-theme', 'dark');
                    }}
                    type="button"
                  >
                    <IoMoonOutline style={{ marginRight: 4 }} /> Escuro
                  </button>
                </div>
              </div>
              <div className={styles['demo-panel-group']}>
                <span className={styles['demo-panel-label']}>Status</span>
                <div className={styles['demo-panel-buttons']}>
                  {(
                    [
                      { status: 'PENDING', label: 'Pendente', color: '#ff9500' },
                      { status: 'OVERDUE', label: 'Vencida', color: '#ff3b30' },
                      { status: 'CONFIRMED', label: 'Paga', color: '#34c759' },
                      { status: 'REFUNDED', label: 'Estornada', color: '#007aff' },
                      { status: 'CANCELLED', label: 'Cancelada', color: '#8e8e93' },
                    ] as { status: FaturaStatus; label: string; color: string }[]
                  ).map((item) => (
                    <button
                      key={item.status}
                      className={`${styles['demo-btn']} ${
                        fatura.status === item.status ? styles['demo-btn-active'] : ''
                      }`}
                      style={
                        fatura.status === item.status
                          ? { borderColor: item.color, color: item.color }
                          : {}
                      }
                      onClick={() => {
                        const isPaidStatus =
                          item.status === 'CONFIRMED' || item.status === 'RECEIVED';
                        setFatura((f) => ({
                          ...f,
                          status: item.status,
                          paymentDate: isPaidStatus
                            ? new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
                                .toISOString()
                                .split('T')[0]
                            : null,
                          billingType: isPaidStatus ? 'PIX' : null,
                        }));
                      }}
                      type="button"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles['demo-panel-group']}>
                <span className={styles['demo-panel-label']}>Cenário</span>
                <div className={styles['demo-panel-buttons']}>
                  <button
                    className={`${styles['demo-btn']} ${
                      fatura.payment === 'MENSAL' && (fatura.cpf?.length ?? 0) > 11
                        ? styles['demo-btn-active']
                        : ''
                    }`}
                    onClick={() =>
                      setFatura((f) => ({
                        ...f,
                        payment: 'MENSAL',
                        contafatura: true,
                        description:
                          '(35) 92003-8028 - ICON UNIVERSITÁRIO\n(35) 99812-5541 - CONTROLE 25GB\n(35) 98804-7712 - CONTROLE 15GB\n(35) 99148-3390 - CONTROLE 15GB\n(35) 99761-0824 - CONTROLE 15GB\n(62) 99445-1182 - CONTROLE 25GB',
                        value: 615.0,
                        nome: 'MGF Sul Construtora e Serviços Ltda',
                        cpf: '10617255000151',
                        email: 'mgfsul@hotmail.com',
                        msisdn: '5535920038028',
                        plandescription: 'ICON UNIVERSITÁRIO',
                      }))
                    }
                    type="button"
                  >
                    PJ (6 linhas)
                  </button>
                  <button
                    className={`${styles['demo-btn']} ${
                      fatura.payment === 'MENSAL' && (fatura.cpf?.length ?? 0) <= 11
                        ? styles['demo-btn-active']
                        : ''
                    }`}
                    onClick={() =>
                      setFatura((f) => ({
                        ...f,
                        payment: 'MENSAL',
                        contafatura: true,
                        description: '(61) 99988-4455 - CONTROLE 25GB',
                        value: 59.9,
                        nome: 'Maria da Silva Santos',
                        cpf: '12345678901',
                        email: 'maria.santos@email.com',
                        msisdn: '5561999884455',
                        plandescription: 'CONTROLE 25GB',
                      }))
                    }
                    type="button"
                  >
                    PF (1 linha)
                  </button>
                  <button
                    className={`${styles['demo-btn']} ${
                      fatura.payment === 'ADICIONAL' ? styles['demo-btn-active'] : ''
                    }`}
                    onClick={() =>
                      setFatura((f) => ({
                        ...f,
                        payment: 'ADICIONAL',
                        contafatura: true,
                        description: 'Recarga avulsa - Crédito R$ 30,00',
                        value: 30.0,
                        nome: 'João Pedro Oliveira',
                        cpf: '98765432100',
                        email: 'joao.pedro@gmail.com',
                        msisdn: '5561998765432',
                        plandescription: 'Controle 15GB',
                      }))
                    }
                    type="button"
                  >
                    Adicional
                  </button>
                </div>
              </div>
              <div className={styles['demo-panel-group']}>
                <span className={styles['demo-panel-label']}>Telas</span>
                <div className={styles['demo-panel-buttons']}>
                  {(
                    [
                      { view: 'fatura', label: 'Fatura' },
                      { view: 'redirect', label: 'Redirect' },
                      { view: 'fallback', label: 'Fallback' },
                      { view: 'erro', label: 'Erro' },
                    ] as { view: DemoView; label: string }[]
                  ).map((item) => (
                    <button
                      key={item.view}
                      className={`${styles['demo-btn']} ${
                        demoView === item.view ? styles['demo-btn-active'] : ''
                      }`}
                      onClick={() => setDemoView(item.view)}
                      type="button"
                    >
                      {item.label}
                    </button>
                  ))}
                  <a
                    className={styles['demo-btn']}
                    href="/rota-inexistente"
                    style={{ textDecoration: 'none' }}
                  >
                    NotFound
                  </a>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
